import type { App } from '../../app.js';
import { h, button, toast, frag } from '../dom.js';
import { allTrackDefs, RaceMode, BotDifficulty, TrackDef } from '@voidline/shared';
import { ENVIRONMENTS } from '../../render/environments/index.js';

const DIFF_LABELS = ['', 'BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'];
const MEDAL_ICONS: Record<string, string> = { bronze: '🥉', silver: '🥈', gold: '🥇', author: '◆' };

export function buildSoloScreen(app: App): HTMLElement {
  const level = app.playerLevel();
  const tracks = allTrackDefs();
  let selTrack: TrackDef = tracks.find((t) => t.unlockLevel <= level) ?? tracks[0];
  let mode: RaceMode = 'race';
  let laps = selTrack.defaultLaps;
  let botCount = 5;
  let botDiff: BotDifficulty = 'standard';
  let seedInput = '';

  const trackList = h('div', {
    class: 'col',
    style: 'gap:8px; max-height: 56vh; overflow-y:auto; padding-right: 8px; min-width: 380px;',
  });
  const detail = h('div', { class: 'col', style: 'min-width: 360px; gap: 14px;' });

  const renderTracks = () => {
    trackList.innerHTML = '';
    for (const t of tracks) {
      const locked = t.unlockLevel > level;
      const rec = app.progression.records[t.id];
      const medal = rec?.medal ? (MEDAL_ICONS[rec.medal] ?? '') : '';
      const env = ENVIRONMENTS[t.environment];
      const b = h(
        'button',
        {
          class: `focusable${t.id === selTrack.id ? ' selected' : ''}`,
          style: 'text-align:left; padding: 10px 16px; opacity:' + (locked ? '0.45' : '1'),
          onclick: () => {
            if (locked) {
              toast(`Reach level ${t.unlockLevel} to unlock ${t.name}.`, true);
              app.audio.play('ui_back');
              return;
            }
            selTrack = t;
            laps = t.defaultLaps;
            app.audio.play('ui_move');
            renderTracks();
            renderDetail();
          },
        },
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between; width:100%;' },
          h('span', { text: `${t.name} ${medal}` }),
          h('span', {
            class: 'subtle',
            style: 'font-size:10px;',
            text: locked ? `LV ${t.unlockLevel}` : DIFF_LABELS[t.difficulty],
          })
        ),
        h('div', {
          class: 'subtle',
          style: 'font-size:11px; text-transform:none; letter-spacing:0.03em; font-weight:400; margin-top:2px;',
          text: `${env?.label ?? t.environment} · ${t.layout.toUpperCase()}`,
        })
      );
      trackList.appendChild(b);
    }
  };

  const renderDetail = () => {
    detail.innerHTML = '';
    const rec = app.progression.records[selTrack.id];
    const modes: { id: RaceMode; label: string }[] = [
      { id: 'race', label: 'CIRCUIT RACE' },
      { id: 'timetrial', label: 'TIME TRIAL' },
      { id: 'elimination', label: 'ELIMINATION' },
    ];
    if (selTrack.layout === 'sprint') modes[0] = { id: 'sprint', label: 'SPRINT' };
    if (!modes.some((m) => m.id === mode)) mode = modes[0].id;

    detail.append(frag(
      h('div', { class: 'h1', text: selTrack.name }),
      h('div', {
        class: 'subtle',
        style: 'text-transform:none; letter-spacing:0.03em;',
        text: selTrack.tagline,
      }),
      h('div', {
        class: 'subtle mono',
        style: 'font-size: 11px;',
        text:
          `MEDALS  ◆ ${selTrack.medals.author}s   🥇 ${selTrack.medals.gold}s   🥈 ${selTrack.medals.silver}s   🥉 ${selTrack.medals.bronze}s` +
          (rec?.bestLapTicks ? `\nYOUR BEST LAP ${(rec.bestLapTicks / 60).toFixed(2)}s` : ''),
      }),
      h('div', { class: 'h2', text: 'MODE', style: 'margin-top:6px;' }),
      h(
        'div',
        { class: 'row wrap' },
        ...modes.map((m) =>
          button(
            m.label,
            () => {
              mode = m.id;
              app.audio.play('ui_move');
              renderDetail();
            },
            `small focusable${mode === m.id ? ' selected' : ''}`
          )
        )
      ),
      mode !== 'sprint' && mode !== 'timetrial'
        ? h(
            'div',
            { class: 'row', style: 'gap:20px;' },
            h(
              'div',
              {},
              h('div', { class: 'h2', text: 'LAPS' }),
              h(
                'div',
                { class: 'row' },
                ...[1, 2, 3, 5].map((n) =>
                  button(
                    String(n),
                    () => {
                      laps = n;
                      app.audio.play('ui_move');
                      renderDetail();
                    },
                    `small focusable${laps === n ? ' selected' : ''}`
                  )
                )
              )
            )
          )
        : null,
      mode !== 'timetrial'
        ? h(
            'div',
            {},
            h('div', { class: 'h2', text: `RIVALS · ${botCount}` }),
            h(
              'div',
              { class: 'row' },
              ...[1, 3, 5, 7].map((n) =>
                button(
                  String(n),
                  () => {
                    botCount = n;
                    app.audio.play('ui_move');
                    renderDetail();
                  },
                  `small focusable${botCount === n ? ' selected' : ''}`
                )
              )
            ),
            h('div', { class: 'h2', text: 'RIVAL SKILL', style: 'margin-top:10px;' }),
            h(
              'div',
              { class: 'row wrap' },
              ...(['cruise', 'standard', 'hostile', 'nemesis'] as BotDifficulty[]).map((d) =>
                button(
                  d.toUpperCase(),
                  () => {
                    botDiff = d;
                    app.audio.play('ui_move');
                    renderDetail();
                  },
                  `small focusable${botDiff === d ? ' selected' : ''}`
                )
              )
            )
          )
        : h(
            'div',
            { class: 'col', style: 'gap:8px;' },
            h('div', {
              class: 'subtle',
              style: 'text-transform:none;',
              text: app.settings.showGhost
                ? 'Your best ghost will race alongside you.'
                : 'Ghosts disabled in settings.',
            }),
            h(
              'div',
              { class: 'row wrap' },
              button(
                'EXPORT GHOST',
                async () => {
                  const { loadGhost } = await import('../../persist.js');
                  const { exportGhost } = await import('../../persist.js');
                  const g = loadGhost(selTrack.id, selTrack.seed ?? null);
                  if (!g) return toast('No ghost saved for this track yet.', true);
                  try {
                    await navigator.clipboard.writeText(exportGhost(g));
                    toast('Ghost copied to clipboard — send it to a rival.');
                  } catch {
                    toast('Clipboard blocked by browser.', true);
                  }
                },
                'small focusable'
              ),
              button(
                'IMPORT GHOST',
                async () => {
                  const { importGhost, saveGhost } = await import('../../persist.js');
                  const text = window.prompt('Paste a VOIDLINE ghost:');
                  if (!text) return;
                  const g = importGhost(text.trim());
                  if (!g) return toast('Not a valid ghost (or from an incompatible version).', true);
                  if (g.trackId !== selTrack.id) {
                    return toast(`That ghost is for "${g.trackId}", not this track.`, true);
                  }
                  saveGhost(g);
                  toast(`Ghost "${g.name}" installed — race it in time trial.`);
                },
                'small focusable'
              )
            )
          ),
      h(
        'div',
        { class: 'row', style: 'margin-top:16px;' },
        button(
          'LAUNCH',
          () =>
            app.startSoloRace({
              trackId: selTrack.id,
              trackSeed: selTrack.seed ?? null,
              mode,
              laps,
              botCount,
              botDifficulty: botDiff,
            }),
          'primary focusable'
        )
      )
    ));
  };

  renderTracks();
  renderDetail();

  // seeded custom track row
  const seedField = h('input', {
    type: 'text',
    placeholder: 'SEED (e.g. NEBULA-7)',
    maxlength: '32',
    style: 'width: 200px; font-size: 12px;',
  }) as HTMLInputElement;
  seedField.addEventListener('input', () => (seedInput = seedField.value));

  return h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel', style: 'max-width: 900px; width: 92vw;' },
      h(
        'div',
        { class: 'row', style: 'justify-content:space-between; margin-bottom: 18px;' },
        h('div', { class: 'h1', text: 'RACE SETUP' }),
        button('◄ BACK', () => {
          app.audio.play('ui_back');
          app.show('menu');
        }, 'small focusable')
      ),
      h('div', { class: 'row', style: 'gap: 34px; align-items:flex-start;' }, trackList, detail),
      h(
        'div',
        { class: 'row', style: 'margin-top: 18px; gap: 12px; border-top: 1px solid var(--line); padding-top: 14px;' },
        h('span', { class: 'h2', text: 'UNCHARTED SPACE' }),
        seedField,
        button(
          'GENERATE & RACE',
          () => {
            const seed = (seedInput.trim() || Math.random().toString(36).slice(2, 8)).slice(0, 32);
            app.startSoloRace({
              trackId: `seed:${seed}`,
              trackSeed: seed,
              mode: 'race',
              laps: 3,
              botCount,
              botDifficulty: botDiff,
            });
          },
          'small focusable'
        ),
        h('span', {
          class: 'subtle',
          style: 'font-size:11px; text-transform:none;',
          text: 'Deterministic from the seed — share it with friends.',
        })
      )
    )
  );
}
